/**
 * IntakeInterviewPage — 7-question adaptive intake interview
 *
 * Replaces the traditional onboarding welcome screen with a conversational
 * interview that builds a user profile to determine which financial fields
 * are relevant. One question per screen on mobile with progress tracking.
 */

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/cn";
import { useAuth } from "../../hooks/useAuth";
import {
  INTAKE_QUESTIONS,
  createDefaultIntakeProfile,
} from "../../types/intake";
import type { IntakeProfile } from "../../types/intake";
import { saveIntakeProfile } from "../../services/intakeService";
import IntakeQuestionCard from "../../components/onboarding/IntakeQuestionCard";
import { createLogger } from "../../utils/logger";

const intakeLogger = createLogger({ prefix: "IntakeInterview" });

const TOTAL_QUESTIONS = INTAKE_QUESTIONS.length;

export default function IntakeInterviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [isSaving, setIsSaving] = useState(false);

  const currentQuestion = INTAKE_QUESTIONS[currentStep];
  const currentValues = answers[currentQuestion.id] ?? [];
  const isFirstQuestion = currentStep === 0;
  const isLastQuestion = currentStep === TOTAL_QUESTIONS - 1;
  const hasSelection = currentValues.length > 0;

  const handleSelect = useCallback(
    (values: string[]) => {
      setAnswers((prev) => ({
        ...prev,
        [currentQuestion.id]: values,
      }));

      // Auto-advance for single-select questions (#475)
      if (!currentQuestion.multiSelect && values.length === 1) {
        const stepWhenSelected = currentStep;
        // Small delay so user sees their selection highlight before advancing
        setTimeout(() => {
          setCurrentStep((prev) => {
            // Only advance if still on the same step (avoid double-advance
            // if user also clicked Next)
            if (prev === stepWhenSelected && prev < TOTAL_QUESTIONS - 1) {
              return prev + 1;
            }
            return prev;
          });
        }, 250);
      }
    },
    [currentQuestion.id, currentQuestion.multiSelect, currentStep]
  );

  const handleNext = useCallback(() => {
    if (!hasSelection) return;
    if (currentStep < TOTAL_QUESTIONS - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [hasSelection, currentStep]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const handleFinish = useCallback(async () => {
    if (!hasSelection || isSaving) return;
    setIsSaving(true);

    // Build the IntakeProfile from answers
    const profile = buildProfileFromAnswers(answers);

    if (user?.userId) {
      const result = await saveIntakeProfile(user.userId, profile);
      if (!result.ok) {
        intakeLogger.error("Failed to save intake profile", {
          userId: user.userId,
          error: result.error,
        });
      }
    }

    navigate("/onboarding/quick-peek", { replace: true });
  }, [hasSelection, isSaving, answers, user, navigate]);

  // Integrate with browser history so the back button goes to the previous
  // intake step instead of leaving the page entirely (#488)
  useEffect(() => {
    if (currentStep === 0) return;

    // Push a history entry for each step so the browser back button works
    window.history.pushState({ intakeStep: currentStep }, "");

    const handlePopState = (event: PopStateEvent) => {
      // If the user presses the browser back button, go to the previous step
      if (
        event.state?.intakeStep !== undefined &&
        event.state.intakeStep < currentStep
      ) {
        setCurrentStep(event.state.intakeStep as number);
      } else {
        setCurrentStep((prev) => Math.max(0, prev - 1));
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [currentStep]);

  return (
    <div className="intake-interview-page" id="intake-interview">
      {/* Progress indicator */}
      <div className="intake-progress" id="intake-progress">
        <span className="intake-progress-text">
          {currentStep + 1} of {TOTAL_QUESTIONS}
        </span>
        <div className="intake-progress-bar">
          <div
            className="intake-progress-fill"
            style={{
              width: `${((currentStep + 1) / TOTAL_QUESTIONS) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Current question */}
      <IntakeQuestionCard
        question={currentQuestion}
        selectedValues={currentValues}
        onSelect={handleSelect}
      />

      {/* Navigation buttons */}
      <div className="intake-nav" id="intake-nav">
        <button
          type="button"
          className={cn(
            "intake-nav-back",
            isFirstQuestion && "intake-nav-disabled"
          )}
          onClick={handleBack}
          disabled={isFirstQuestion}
          aria-label="Back"
        >
          Back
        </button>

        {isLastQuestion ? (
          <button
            type="button"
            className="intake-nav-finish"
            onClick={handleFinish}
            disabled={!hasSelection || isSaving}
            aria-label="See my plan"
          >
            {isSaving ? "Saving..." : "See my plan"}
          </button>
        ) : (
          <button
            type="button"
            className="intake-nav-next"
            onClick={handleNext}
            disabled={!hasSelection}
            aria-label="Next"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Build an IntakeProfile from the interview answers map.
 * Maps each question's answer to the corresponding profile field.
 */
function buildProfileFromAnswers(
  answers: Record<string, string[]>
): IntakeProfile {
  const defaults = createDefaultIntakeProfile();

  return {
    ...defaults,
    completedAt: new Date(),
    lifeStage:
      (answers.q1?.[0] as IntakeProfile["lifeStage"]) ?? defaults.lifeStage,
    household:
      (answers.q2?.[0] as IntakeProfile["household"]) ?? defaults.household,
    housing: (answers.q3?.[0] as IntakeProfile["housing"]) ?? defaults.housing,
    employment:
      (answers.q4?.[0] as IntakeProfile["employment"]) ?? defaults.employment,
    investmentLevel:
      (answers.q5?.[0] as IntakeProfile["investmentLevel"]) ??
      defaults.investmentLevel,
    debtSituation:
      (answers.q6 as IntakeProfile["debtSituation"]) ?? defaults.debtSituation,
    nearTermEvents:
      (answers.q6b as IntakeProfile["nearTermEvents"]) ??
      defaults.nearTermEvents,
    knowledgeLevel:
      (answers.q7?.[0] as IntakeProfile["knowledgeLevel"]) ??
      defaults.knowledgeLevel,
  };
}
